import * as React from "react";
import { faSpinner } from "@insidebeehive/fortawesome/pro-solid-svg-icons/faSpinner";

import { Label } from "./label";
import { Icon } from "./icon";

import { cn } from "~/lib/utils";

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  isKbd?: boolean;
  isHidden?: boolean;
  showSpinner?: boolean;
  setShowSpinner?: React.Dispatch<React.SetStateAction<boolean>>;
  reset?: string;
  onClose?: () => void;
  suffixIcon?: React.ReactNode;
  inputPrefix?: React.ReactNode;
  isRequired?: boolean;
  inputPrefixClassName?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(({ className, type, error, showSpinner, setShowSpinner, id, isKbd, label, isHidden, suffixIcon, inputPrefix, isRequired, inputPrefixClassName, ...props }, ref) => {
  const generatedId = React.useId();
  const elementId = id || generatedId;

  return (
    <div className="w-full relative">
      <Label htmlFor={elementId}>
        {label} {isRequired ? <sup className="-ml-1 font-bold text-red-400">*</sup> : ""}
      </Label>
      {!isHidden && (
        <div className="flex items-center relative">
          {inputPrefix && <div className={cn("flex items-center justify-center h-9 px-3 py-1 text-sm text-muted-foreground border border-r-0 border-input bg-muted/50 rounded-l-md", inputPrefixClassName)}>{inputPrefix}</div>}
          <input
            type={type}
            id={elementId}
            hidden={isHidden}
            className={cn(
              "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none  disabled:cursor-not-allowed disabled:opacity-50",
              inputPrefix && "rounded-l-none",
              error && "border-destructive text-destructive placeholder:text-destructive/50 focus:ring-1 focus:ring-inset ring-destructive focus:ring-destructive",
              className,
            )}
            ref={ref}
            {...props}
          />
          {suffixIcon}
        </div>
      )}
      {(showSpinner || isKbd) && <div className="absolute inset-y-0 top-1/2 transform -translate-y-1/2 right-4 flex items-center cursor-pointer">{showSpinner ? <Icon icon={faSpinner} className="text-primary animate-spin" name="loader-solid" /> : <></>}</div>}
      {/* {isKbd && <kbd className="absolute top-1/2 right-4 border-1 transform -translate-y-1/2 text-xs text-gray-400">Ctrl + K</kbd>} */}
      {error && <div className={cn(`${error ? "text-destructive w-full ml-1 mt-1" : null} text-sm`, className)}>{error}</div>}
    </div>
  );
});

Input.displayName = "Input";

export { Input };
